import { useMutation } from "@apollo/client";
import type { UserSettingsShape } from "components/settings/UserSettingsSchema";
import { UserSettingsSchema } from "components/settings/UserSettingsSchema";
import { userSettingsAtom } from "components/settings/atoms";
import { useAtom } from "jotai";
import ChangeUserSettingMutation from "lib/graphql/mutations/settings/ChangeUserSettingMutation";
import type {
  IChangeUserSettingMutation,
  IChangeUserSettingMutationVariables,
} from "lib/graphql/mutations/settings/__generated__/ChangeUserSettingMutation.generated";
import useSelfie from "lib/hooks/useSelfie";
import type { Path, PathValue } from "lib/types/path";
import { has } from "lodash";
import get from "lodash/get";
import set from "lodash/set";
import { startTransition } from "react";
import { useDebounceCallback } from "usehooks-ts";

export function useSpecificSetting<
  Key extends Path<UserSettingsShape>,
  ValueForPath extends PathValue<UserSettingsShape, Key>,
  DefaultValueForPath extends PathValue<UserSettingsShape, Key>,
  ValueFn extends (currentSettingValue: PathValue<UserSettingsShape, Key>) => PathValue<UserSettingsShape, Key>,
>(key: Key, defaultNotSet: DefaultValueForPath | null = null) {
  const [localSettings, setSettings] = useAtom(userSettingsAtom);
  const viewer = useSelfie();

  const settingsFromViewer = UserSettingsSchema.partial().parse(viewer?.settingsV2 || {});
  const currentValue = get(settingsFromViewer, key) as ValueForPath | undefined;

  const [mutate, { loading }] = useMutation<IChangeUserSettingMutation, IChangeUserSettingMutationVariables>(
    ChangeUserSettingMutation,
  );
  const mutateWithDebounce = useDebounceCallback(mutate, 300);
  const hasLocalSettingCopy = has(localSettings, key);

  const setSettting = async (valueOrValueProducerFn: ValueFn | ValueForPath) => {
    if (viewer) {
      const existingSettings = UserSettingsSchema.parse(structuredClone(viewer.settingsV2));
      const valueToSubmit =
        typeof valueOrValueProducerFn === "function"
          ? valueOrValueProducerFn(get(settingsFromViewer, key))
          : valueOrValueProducerFn;
      const valueToSet =
        typeof valueOrValueProducerFn === "function"
          ? valueOrValueProducerFn(get(localSettings, key) as ValueForPath)
          : valueOrValueProducerFn;
      setSettings(set(existingSettings, key, valueToSet));
      startTransition(() => {
        mutateWithDebounce({
          variables: {
            key,
            value: valueToSubmit,
          },
        });
      });
      return null;
    }

    const valueToSet =
      typeof valueOrValueProducerFn === "function"
        ? valueOrValueProducerFn(get(localSettings, key) as ValueForPath)
        : valueOrValueProducerFn;
    const modifiedSettings = set(localSettings, key, valueToSet);
    setSettings(modifiedSettings);
    return null;
  };

  if (hasLocalSettingCopy) {
    return {
      value: get(localSettings, key),
      set: setSettting,
      loading,
    };
  }
  if (viewer) {
    return {
      value: viewer ? currentValue : defaultNotSet,
      set: setSettting,
      loading,
    };
  }
  return {
    value: defaultNotSet,
    set: setSettting,
    loading,
  };
}
